import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Checkbox,
  Grid,
  TextField,
  Collapse,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormControlLabel,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { connect } from 'react-redux';
import {
  getEntitiesRequest,
  getEntityGlobalsRequest,
} from '../../../redux/actions/getEntity';
import { GameCssStyles } from '../GameCssStyles';
import MediaSelectDialog from './MediaSelectDialog';
import { generatePublicUrlForFileInAWSS3Bucket } from '../../../services/uploadFileOnAWS_S3_Bucket';

function EntertainmentEntityDialogV2(props) {
  const { open, onClose, onSubmit, formGameData, selectedItems, handleChange } = props;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1949 }, (_, i) => 1950 + i);

  const LIMIT = 100;
  const hasFetchedEntityGlobals = useRef(false);
  const scrollRef = useRef(null);

  const [fetchedPages, setFetchedPages] = useState(new Set([1]));
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState({});
  const totalPages = paginationData?.totalPages || 1;

  // 🔹 Filters
  const [showFilter, setShowFilter] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [artistTypeFilter, setArtistTypeFilter] = useState('');
  const [isSuperStarFilter, setIsSuperStarFilter] = useState(false);

  const [openMediaDialog, setOpenMediaDialog] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedEntertainmentEntity, setSelectedEntertainmentEntity] = useState(null);
  const [entertainment_entity_data, setEntertainmentEntity] = useState([]);
  const [entertainment_entity_filter, setEntertainmentEntityFilter] = useState(null);

  const [selectedIds, setSelectedIds] = useState([]);


  // 🔹 Load initial entities
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setFetchedPages(new Set([1]));
      setEntertainmentEntity([]);
      props?.getEntities({ page: 1, limit: LIMIT });
    }
  }, [open]);

  // 🔹 Load globals once
  useEffect(() => {
    if (!hasFetchedEntityGlobals.current && !props?.ENTITY_GLOBALS_DATA && !props?.ENTITY_GLOBALS_ERROR) {
      hasFetchedEntityGlobals.current = true;
      props?.getEntityGlobals();
    } else {
      setEntertainmentEntityFilter(props?.ENTITY_GLOBALS_DATA);
    }
  }, [props?.ENTITY_GLOBALS_DATA, props?.ENTITY_GLOBALS_ERROR]);

  // 🔹 Append new data with unique check
  useEffect(() => {
    if (props?.ENTITIES_DATA) {
      setPaginationData(props?.ENTITIES_PAGINATION_DATA || {});
      setEntertainmentEntity((prev) => {
        const merged = [...prev, ...props.ENTITIES_DATA];
        const unique = Array.from(new Map(merged.map((item) => [item._id, item])).values());
        return unique;
      });
    }
  }, [props?.ENTITIES_DATA, props?.ENTITIES_PAGINATION_DATA]);

  // 🔹 Infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const isBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
    if (isBottom && currentPage < totalPages && !fetchedPages.has(currentPage + 1)) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setFetchedPages((prev) => new Set(prev).add(nextPage));
      props?.getEntities({
        page: nextPage,
        limit: LIMIT,
        displayName: searchText,
        type: typeFilter,
        language: languageFilter,
        country: countryFilter,
        industry: industryFilter,
        artistTypeFilter: artistTypeFilter,
        year: yearFilter
      });
    }
  }, [currentPage, totalPages, fetchedPages, props, searchText]);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) container.addEventListener('scroll', handleScroll);
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // 🔹 Search
  const handleSearch = () => {
    setCurrentPage(1);
    setFetchedPages(new Set([1]));
    setEntertainmentEntity([]);
    props?.getEntities({
      page: 1,
      limit: LIMIT,
      displayName: searchText,
      type: typeFilter,
      language: languageFilter,
      country: countryFilter,
      industry: industryFilter,
      artistTypeFilter: artistTypeFilter,
      year: yearFilter
    });
  };

  const handleClearSearch = () => {
    setSearchText('');
    setTypeFilter('');
    setLanguageFilter('');
    setIndustryFilter('');
    setCountryFilter('');
    setArtistTypeFilter('');
    setYearFilter('');
    setCurrentPage(1);
    setFetchedPages(new Set([1]));
    setEntertainmentEntity([]);
    props?.getEntities({ page: 1, limit: LIMIT });
  };

  const handleSubmit = () => {
    //const selectedItems = entertainment_entity_data.filter((item) => selectedIds.includes(item?._id));
    //onSubmit?.(formGameData?.enti);
    //setSelectedIds(selectedItems);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        maxWidth="md"
        sx={{ m: 3 }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: 0,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{
          px: 3,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
        }}>
          <Typography fontWeight="bold" sx={{ flex: 1, fontSize: 20 }}>
            Entertainment Entities
          </Typography>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', justifyContent: 'space-between' }}>

            <Box sx={{ flex: 0.9, flexDirection: 'row', display: 'flex', gap: 2, mr: 2, mt: 1, mb: 0 }}>
              <TextField
                fullWidth
                label="Search by name"
                variant="outlined"
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={GameCssStyles.formSingleLineTextField_sx}
              />
              <Button variant="contained" onClick={handleSearch}>
                Search
              </Button>
              <Button variant="outlined" onClick={handleClearSearch}>
                Clear
              </Button>
            </Box>

            <Box sx={{ flex: 0.1, display: 'flex', gap: 1, ml: 0, justifyContent: 'flex-end', }}>
              <IconButton onClick={() => {
                if (showFilter) {
                  setTypeFilter('');
                }
                setShowFilter(!showFilter)
              }}>
                <FilterListIcon />
              </IconButton>
              <IconButton onClick={() => {
                console.log("clear")
                setTypeFilter('');
                onClose()
              }}>
                <CloseIcon />
              </IconButton>
            </Box>

          </Box>
          <Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers={false} sx={{ p: 0, overflow: "hidden" }}>

          <Box sx={{
            height: '100%',
            backgroundColor: '#f4f1f9',
            justifyContent: 'center',
            display: "flex",
            flex: 1,
            flexGrow: 1,
            flexDirection: "row",
            gap: 0,
            overflow: "hidden",
          }}>

            <Box
              ref={scrollRef}
              sx={{
                m: 2, p: 2, height: '100%', width: '100%',
                overflowY: 'auto',
              }}>
              <Grid container spacing={2}
                sx={{
                  width: '100%',
                }}>
                {[...entertainment_entity_data]
                  .sort((a, b) => {
                    const aSelected = selectedItems.some(sel => sel.entity._id === a._id);
                    const bSelected = selectedItems.some(sel => sel.entity._id === b._id);

                    if (aSelected && !bSelected) return -1; // ✅ selected first
                    if (!aSelected && bSelected) return 1;
                    return 0; // keep original order for same group
                  })?.map((item) => {
                    //console.log("item : ", item);
                    //console.log(`item2 : ${item?.displayName}`, Object.keys(item?.selectedMedia ?? {}).length > 0);

                    const isSelected = selectedItems.some(sel => sel.entity._id === item._id);
                    const multimedia = selectedItems.find(sel => sel.entity._id === item._id)?.media?.url;
                    return (
                      <Grid item key={item?._id} xs={12} sm={6} md={4} lg={3}>
                        <Box
                          sx={{
                            height: 140,
                            width: 240,
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: 'whitesmoke',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            display: 'flex',
                            alignItems: 'flex-end',
                            padding: 1,
                            border: isSelected ? '2px solid #1976d2' : '1px solid #ccc',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                          onClick={() => {
                            //handleToggle(item._id)
                            setSelectedEntertainmentEntity(item);
                            setOpenMediaDialog(true);
                          }}>
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 2,
                              overflow: 'hidden',
                              backgroundImage: `url(${generatePublicUrlForFileInAWSS3Bucket(multimedia)})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              mr: 2,
                              flexShrink: 0,
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                            onClick={() => {
                              if (item?.multimedia) {
                                setSelectedEntertainmentEntity(item);
                                setOpenMediaDialog(true);
                                //setOpenMediaDialog(true);
                              }
                            }}
                          />

                          <Box sx={{ flexGrow: 1 }}>
                            <Typography fontWeight="bold" sx={{ fontSize: 12 }}>
                              {item?.displayName}
                            </Typography>
                            <Typography variant="body2" noWrap>
                              {item?.genres?.join(', ') || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item?.type}
                            </Typography>
                          </Box>

                        </Box>
                      </Grid>
                    )
                  })}
              </Grid>
            </Box>

            {showFilter && (
              <Box
                sx={{
                  height: '100%',
                  width: '25%',
                  borderLeft: '1px solid #ccc',
                  borderTop: '1px solid #ccc',
                  borderBottom: '1px solid #ccc',
                  backgroundColor: '#f9f9f9',
                  p: 2,
                  ml: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                  <Typography variant="h6" gutterBottom>Filters</Typography>

                  {/* Type Dropdown */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      {entertainment_entity_filter?.EntertainmentEntityTypesV2?.map((type) => {
                        console.log("type : ", type);
                        return (
                          <MenuItem key={type} value={type?.value}>
                            {type?.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Language</InputLabel>
                    <Select
                      value={languageFilter}
                      onChange={(e) => setLanguageFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      {entertainment_entity_filter?.language?.map((type) => {
                        console.log("type : ", type);
                        return (
                          <MenuItem key={type} value={type?.value}>
                            {type?.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Industry</InputLabel>
                    <Select
                      value={industryFilter}
                      onChange={(e) => setIndustryFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      {entertainment_entity_filter?.industry?.map((type) => {
                        console.log("type : ", type);
                        return (
                          <MenuItem key={type} value={type?.value}>
                            {type?.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={countryFilter}
                      onChange={(e) => setCountryFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      {entertainment_entity_filter?.country?.map((type) => {
                        console.log("type : ", type);
                        return (
                          <MenuItem key={type} value={type?.value}>
                            {type?.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Artist Type</InputLabel>
                    <Select
                      value={artistTypeFilter}
                      onChange={(e) => setArtistTypeFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All</MenuItem>
                      {entertainment_entity_filter?.ArtistType?.map((type) => {
                        console.log("type : ", type);
                        return (
                          <MenuItem key={type} value={type?.value}>
                            {type?.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Year</InputLabel>
                    <Select
                      label="Year"
                      value={yearFilter ?? ''}
                      onChange={(e) =>
                        setYearFilter(e.target.value)
                      }
                    >
                      {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Year Range</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>From</InputLabel>
                    <Select
                      label="From"
                      value={yearFrom ?? ''}
                      onChange={(e) =>
                        setYearFrom(e.target.value)
                      }
                    >
                      {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>To</InputLabel>
                    <Select
                      label="To"
                      value={yearTo ?? ''}
                      onChange={(e) =>
                        setYearTo(e.target.value)
                      }
                    >
                      {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box> */}

                  {/* <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!isSuperStarFilter}
                        onChange={(e) => {
                          setIsSuperStarFilter(e.target.checked);
                        }}
                      />
                    }
                    label={"Super Star"}
                    style={{ marginTop: 8 }}
                  /> */}

                </Box>

                <Box sx={{ mt: "auto", display: "flex", flexDirection: "row", gap: 1 }}>
                  <Button variant="contained" fullWidth onClick={handleClearSearch} sx={{ height: 35, fontSize: 12, fontWeight: '400', backgroundColor: 'red', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                    Clear
                  </Button>

                  <Button variant="contained" fullWidth onClick={handleSearch} sx={{ height: 35, fontSize: 12, fontWeight: '400', backgroundColor: 'burlywood', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                    FIlter
                  </Button>
                </Box>

              </Box>
            )}

          </Box>
        </DialogContent>

        <DialogActions dividers={false} sx={{ p: 0 }}>
          <Button variant="contained" fullWidth onClick={handleSubmit} sx={{ height: 50, fontSize: 16, fontWeight: '600', backgroundColor: 'burlywood', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
            Submit ({selectedItems?.length})
          </Button>
        </DialogActions>
      </Dialog>

      {selectedEntertainmentEntity && <MediaSelectDialog
        open={openMediaDialog}
        onClose={() => {
          setOpenMediaDialog(false)
          setSelectedMedia(null);
          setSelectedEntertainmentEntity(null);
        }}
        onSubmit={(selectedMedia, multimedia) => {
          console.log("selectedMedia : ", selectedMedia);
          console.log("selectedEntertainmentEntity : ", selectedEntertainmentEntity);

          const isExists = selectedItems.some(sel => sel.entity._id === selectedMedia._id);

          const updated_entityMedia = selectedItems.some(
            sel => sel.entity._id === selectedEntertainmentEntity._id
          )
            ? selectedItems.map(sel =>
              sel.entity._id === selectedEntertainmentEntity._id
                ? { entity: selectedEntertainmentEntity, media: selectedMedia }
                : sel
            )
            : [...selectedItems, { entity: selectedEntertainmentEntity, media: selectedMedia }];

          //const updated_entityMedia = [...selectedItems, { entity: selectedEntertainmentEntity, media: selectedMedia }]

          const mediaEntity = updated_entityMedia?.map(item => ({
            entityId: item?.entity?._id,
            multimediaId: item?.media?._id
          }));
          handleChange('mediaEntity', mediaEntity)
          handleChange('entityMedia', updated_entityMedia)
          //setSelectedMedia(multimedia?.[0]);
          //setOpenMediaDialog(false);

          // const updatedItem = { ...selectedEntertainmentEntity, selectedMedia: selectedMedia };
          // console.log("updatedItem : ", updatedItem)

          // setSelectedIds((prev) => {
          //   const exists = prev.some(item => item._id === updatedItem._id);
          //   if (exists) {
          //     return prev.map(item =>
          //       item?._id === updatedItem?._id ? updatedItem : item
          //     );
          //   }
          //   return [...prev, updatedItem];
          // });

          // setSelectedEntertainmentEntity((prev) => ({ ...prev, updatedItem }));
          // setEntertainmentEntity((prevList) =>
          //   prevList.map((item) => (item?._id === updatedItem?._id ? updatedItem : item))
          // );

          console.log("selectedIds : ", selectedIds);
        }}
        selectedEntertainmentEntity={selectedEntertainmentEntity}
      />}

    </>
  );
}

const mapStateToProps = (state) => ({
  ENTITY_GLOBALS_DATA: state?.getEntity?.entity_globals_data,
  ENTITY_GLOBALS_ERROR: state?.getEntity?.entity_globals_error,
  ENTITIES_DATA: state?.getEntity?.entities_data,
  ENTITIES_PAGINATION_DATA: state?.getEntity?.entities_meta,
});

const mapDispatchToProps = {
  getEntities: getEntitiesRequest,
  getEntityGlobals: getEntityGlobalsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(EntertainmentEntityDialogV2);
